import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminFunctions.css";

const AdminFunctions = ({ functions = [], selectedProject }) => {
  const navigate = useNavigate();

  const [countsByApi, setCountsByApi] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const visibleFunctions = useMemo(() => {
    const query = search.trim().toLowerCase();

    return functions
      .filter((item) => item.hidden !== true && item.hidden !== "true")
      .filter((item) => item.name?.toLowerCase().includes(query));
  }, [functions, search]);

  const groupedFunctions = useMemo(() => {
    return visibleFunctions.reduce((groups, item) => {
      const category = item.category || "Other";

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push(item);
      return groups;
    }, {});
  }, [visibleFunctions]);

  const getCategoryColor = (category = "Other") => {
    let hash = 0;

    for (let index = 0; index < category.length; index += 1) {
      hash = category.charCodeAt(index) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 78%, 58%)`;
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchCounts = async () => {
      try {
        setLoading(true);

        const countApis = [
          ...new Set(
            visibleFunctions.map((item) => item.countApi).filter(Boolean),
          ),
        ];

        if (!countApis.length) {
          setCountsByApi({});
          return;
        }

        const responses = await Promise.allSettled(
          countApis.map((api) => {
            const url = selectedProject
              ? `${api}${api.includes("?") ? "&" : "?"}project=${selectedProject}`
              : api;

            return fetch(url, { signal: controller.signal })
              .then((response) => (response.ok ? response.json() : null))
              .catch(() => null);
          }),
        );

        const nextCounts = {};

        responses.forEach((response, index) => {
          const api = countApis[index];

          if (response.status !== "fulfilled" || !response.value) {
            nextCounts[api] = null;
            return;
          }

          const data = response.value;

          nextCounts[api] =
            typeof data === "number"
              ? data
              : (data.count ??
                data.total ??
                (() => {
                  const firstNumber = Object.values(data).find(
                    (value) => !Number.isNaN(Number(value)),
                  );

                  return firstNumber !== undefined ? Number(firstNumber) : null;
                })());
        });

        setCountsByApi(nextCounts);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();

    return () => {
      controller.abort();
    };
  }, [visibleFunctions, selectedProject]);

  const formatCount = (value) => {
    if (value === null || value === undefined) return "—";
    return value > 999 ? "999+" : value;
  };

  const openModule = (link) => {
    if (!link) return;
    navigate(link);
  };

  const handleModuleKeyDown = (event, link) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openModule(link);
    }
  };

  return (
    <div className="safety-admin-functions">
      <div className="safety-admin-functions__toolbar">
        <div className="safety-admin-functions__search">
          <i className="fas fa-search safety-admin-functions__search-icon" />

          <input
            type="text"
            placeholder="Search modules..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="safety-admin-functions__search-input"
          />
        </div>

        <div className="safety-admin-functions__meta">
          {visibleFunctions.length} module
          {visibleFunctions.length === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <div className="safety-admin-functions__loading">
          <span className="safety-admin-functions__loader" />
          Loading modules...
        </div>
      ) : visibleFunctions.length === 0 ? (
        <div className="safety-admin-functions__empty">
          <i className="fas fa-layer-group safety-admin-functions__empty-icon" />
          <h3 className="safety-admin-functions__empty-title">
            No modules found
          </h3>
          <p className="safety-admin-functions__empty-text">
            Try another search keyword.
          </p>
        </div>
      ) : (
        Object.entries(groupedFunctions).map(([category, items]) => (
          <section
            key={category}
            className="safety-admin-functions__section"
            style={{ "--category-accent": getCategoryColor(category) }}
          >
            <div className="safety-admin-functions__section-header">
              <h3 className="safety-admin-functions__section-title">
                {category}
              </h3>

              <span className="safety-admin-functions__section-count">
                {items.length}
              </span>
            </div>

            <div className="safety-admin-functions__grid">
              {items.map((item) => {
                const count = item.countApi ? countsByApi[item.countApi] : null;

                return (
                  <div
                    key={item.name}
                    className="safety-admin-functions__card"
                    onClick={() => openModule(item.link)}
                    onKeyDown={(event) => handleModuleKeyDown(event, item.link)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${item.name}`}
                  >
                    <div className="safety-admin-functions__card-content">
                      <div className="safety-admin-functions__card-main">
                        <span className="safety-admin-functions__card-title">
                          {item.name}
                        </span>

                        {count !== null && count !== undefined && (
                          <span className="safety-admin-functions__card-count">
                            {formatCount(count)}
                          </span>
                        )}
                      </div>

                      <div className="safety-admin-functions__icon-box">
                        {item.icon?.startsWith("fa-") ? (
                          <i
                            className={`fas ${item.icon} safety-admin-functions__icon`}
                          />
                        ) : (
                          <img
                            src={item.icon}
                            alt={item.name}
                            className="safety-admin-functions__icon safety-admin-functions__icon--image"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default AdminFunctions;
